// GigDraftManagement.jsx
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Button,
  TablePagination,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReusableCard from "../../components/reusableComponents/ReusableCard";
import ReusableHeadingComponents from "../../components/reusableComponents/reusableHeadingComponents";
import ReusableButton from "../../components/reusableComponents/reusableButtonComponents";
import ApiService from "../../_services/ApiService";
import { CardSkeletonCollab } from "../../components/reusableComponents/SkeletonCard";
import NoResults from "../../components/reusableComponents/NoResults";
import { useHistory } from "react-router-dom";
import bgimage from "../../img/no_image.png";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import Tablecomponent from "../../components/reusableComponents/Tablecomponent";
import GigCard from "./GigCard";

const GigDraftManagement = () => {
  const history = useHistory();
  const [gigList, setGigList] = useState([]);
  const [loader, setLoader] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(""); // 🔹 NEW
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [tabValue, setTabValue] = useState(0);
  const userData = useSelector((state) => state.userData.userData);

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const stripHtml = (html) =>
    typeof html === "string" ? html.replace(/<[^>]+>/g, "") : "";

  const columns = [
    {
      headerName: "Brand",
      field: "BrandLogoURL",
      flex: 0.8,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center">
          <img
            src={row?.BrandLogoURL || bgimage}
            alt="brand-logo"
            width={55}
            height={55}
            style={{
              borderRadius: "8px",
              objectFit: "contain",
              backgroundColor: "#F5F5F5",
            }}
          />
        </Box>
      ),
    },
    {
      headerName: "Gig Title",
      field: "Title",
      flex: 2,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
            {row?.Title || "--"}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#7A7A7A" }}>
            {row?.BrandName || "--"}
          </Typography>
        </Box>
      ),
    },
    {
      headerName: "Description",
      field: "description",
      flex: 1.2,
      renderCell: ({ row }) => {
        const fullText =
          row?.SubTitle ||
          stripHtml(row?.AboutGigWork) ||
          "";

        return (
          <Tooltip
            title={
              <Typography
                sx={{
                  fontSize: "13px",
                  whiteSpace: "pre-line",
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {fullText}
              </Typography>
            }
            placement="bottom"
            arrow
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#757575",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                cursor: "pointer",
                maxWidth: 300,
              }}
            >
              {fullText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Deadline",
      field: "DeadLineDate",
      flex: 1,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "14px", color: "#7A7A7A" }}>
          {row?.DeadLineDate || "--"}
        </Typography>
      ),
    },
    {
      headerName: "Rewards",
      field: "RewardAmount",
      flex: 1,
      renderCell: ({ row }) => (
        <Typography sx={{ fontWeight: 600, color: "#219653" }}>
          {row?.RewardAmount
            ? `₹${Number(row.RewardAmount).toLocaleString("en-IN")}`
            : "--"}
        </Typography>
      ),
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1.2,
      renderCell: ({ row }) => (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              borderColor: "#ED1C24",
              color: "#ED1C24",
            }}
            onClick={() =>
              history.push(`/gig-management/edit-gig/${row?.id}`, {
                eventData: row,
              })
            }
          >
            Edit
          </Button>
          {/* <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              backgroundColor: "#ED1C24",
              "&:hover": { backgroundColor: "#c3080d" },
            }}
            onClick={() =>
              history.push("/gig-management/view-gig", {
                eventData: row,
              })
            }
          >
            View
          </Button> */}
        </Box>
      ),
    },
  ];

  // 🔹 Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // reset page when user changes search
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // ------- FETCH DRAFT GIGS ----------
  const fetchData = () => {
    setLoader(true);

    const params = {
      offset: page,
      limit: rowsPerPage,
      is_draft: true,          // drafts only
      brand_id: userData?.brand,
    };

    // 🔹 add search param if not empty (debounced value)
    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    ApiService.get("gigs", params)
      .then((res) => {
        if (res?.status === 200 || res?.status === 201) {
          setCount(res?.data?.count);
          setGigList(res?.data?.data);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (userData) fetchData();
  }, [userData, page, rowsPerPage, debouncedSearch]); // 🔹 include debouncedSearch

  // server is now doing search, so no need to filter again on client
  const filteredData = gigList;

  return (
    <Box p={4}>
      {/* HEADER */}
      <Box
        display="flex"
        alignItems={{ xs: "none", sm: "center" }}
        justifyContent="space-between"
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          paddingBottom: { xs: "20px", sm: 0 },
        }}
      >
        <ReusableHeadingComponents
          bacHistory={true}
          clickHandlerBack={() => history.push("/gig-management")}
          heading="Gig Drafts"
          subHeading="Manage and review all your draft gigs."
        />

        <Box display="flex" gap={2}>
          <ReusableButton
            children="Create Gig Work"
            variant="contained"
            classNames="gn-actionbtn"
            onClick={() =>
              history.push("/gig-management/create-gig-management")
            }
          />
        </Box>
      </Box>

      <Box
        mt={1}
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <TextField
          placeholder="Search Draft Gig"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: "100%", sm: "500px" }, background: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#7A7A7A" }} />
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" alignItems="center">
          <Tooltip title="Grid View">
            <IconButton
              onClick={() => setViewMode("grid")}
              sx={{
                border: "1px solid #7A7A7A",
                borderRight: "none",
                borderRadius: "8px 0 0 8px",
                width: 40,
                height: 40,
                backgroundColor:
                  viewMode === "grid" ? "#7A7A7A" : "transparent",
                color: viewMode === "grid" ? "#FFFFFF" : "#7A7A7A",
              }}
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Table View">
            <IconButton
              onClick={() => setViewMode("table")}
              sx={{
                border: "1px solid #7A7A7A",
                borderRadius: "0 8px 8px 0",
                width: 40,
                height: 40,
                backgroundColor:
                  viewMode === "table" ? "#7A7A7A" : "transparent",
                color: viewMode === "table" ? "#FFFFFF" : "#7A7A7A",
              }}
            >
              <ViewListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* GRID / TABLE VIEW */}
      {viewMode === "grid" ? (
        <Grid container spacing={2}>
          {loader
            ? Array.from({ length: 8 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                  <CardSkeletonCollab />
                </Grid>
              ))
            : filteredData.length ? (
                filteredData.map((data, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <GigCard
                      gig={data}
                      // onView={() =>
                      //   history.push("/gig-management/view-gig", {
                      //     eventData: data,
                      //   })
                      // }
                      onEdit={() =>
                        history.push(`/gig-management/edit-gig/${data?.id}`, {
                          eventData: data,
                        })
                      }
                    />
                  </Grid>
                ))
              ) : (
                <NoResults eventNoData="No Draft Gig Found" />
              )}
        </Grid>
      ) : (
        <Tablecomponent
          loader={loader}
          columns={columns}
          data={filteredData}
          rowsPerPage={rowsPerPage}
          page={page}
          count={count}
          onPageChange={setPage}
          rowsPerPageOptions={[5, 10, 20]}
          onRowsPerPageChange={(e) =>
            setRowsPerPage(parseInt(e.target.value, 10))
          }
        />
      )}

      {/* Pagination */}
      {viewMode === "grid" && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) =>
            setRowsPerPage(parseInt(e.target.value, 10))
          }
          sx={{
            "& .MuiTablePagination-selectLabel": {
              paddingTop: "14px",
            },
            "& .MuiTablePagination-select": {
              display: "inline-flex",
              alignItems: "center",
              paddingY: "0px",
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
            },
          }}
        />
      )}
    </Box>
  );
};

export default GigDraftManagement;
